import React, { ReactElement } from "react";

import { IAgency, IAgencyAddress } from "models";

import "./AgencyDetails.scss";
import { dataTestIds } from "data-testids";
import { getUrlWithProtocol } from "shared/utils/getUrlWithProtocol";

export interface IAgencyDetailsContent {
  telephoneText: string;
  mobilePhoneText: string;
}

export interface IAgencyDetailsProps {
  agency: IAgency;
  content: IAgencyDetailsContent;
  subHeaderComponent?: ReactElement;
}

interface IAgencyInfo {
  agencyAddress?: IAgencyAddress;
  phone1?: string;
  phone2?: string;
  mobilePhone?: string;
  email?: string;
  website?: string;
}

const getAddress = (address: IAgencyAddress) => {
  const nameParts = [
    address.addressLine1,
    address.addressLine2,
    address.addressLine3,
    address.addressLine4,
    address.country,
    address.postCode,
  ];

  return nameParts.filter((item) => Boolean(item)).join(", ");
};

const renderAgencyInfo = (
  agencyInfo: IAgencyInfo,
  content: IAgencyDetailsContent
) => {
  const address = agencyInfo.agencyAddress
    ? getAddress(agencyInfo.agencyAddress)
    : undefined;

  return (
    <p className="c-agency-details__secondary-text">
      {address && <span>{address}</span>}
      {(agencyInfo.phone1 || agencyInfo.phone2) && (
        <span>
          {`${content.telephoneText}: `}
          {agencyInfo.phone1 && (
            <a
              href={`tel:${agencyInfo.phone1}`}
              className="g-alternative-link g-alternative-link--md"
              tabIndex={0}
            >
              {agencyInfo.phone1}
            </a>
          )}
          {agencyInfo.phone1 && agencyInfo.phone2 && "; "}
          {agencyInfo.phone2 && (
            <a
              href={`tel:${agencyInfo.phone2}`}
              className="g-alternative-link g-alternative-link--md"
              tabIndex={0}
            >
              {agencyInfo.phone2}
            </a>
          )}
        </span>
      )}
      {agencyInfo.mobilePhone && (
        <span>
          {`${content.mobilePhoneText}: `}
          <a
            href={`tel:${agencyInfo.mobilePhone}`}
            className="g-alternative-link g-alternative-link--md"
            tabIndex={0}
          >
            {agencyInfo.mobilePhone}
          </a>
        </span>
      )}
      {agencyInfo.email && (
        <span>
          <a
            href={`mailto:${agencyInfo.email}`}
            className="g-alternative-link g-alternative-link--md"
            tabIndex={0}
          >
            {agencyInfo.email}
          </a>
        </span>
      )}
      {agencyInfo.website && (
        <span>
          <a
            className="g-alternative-link g-alternative-link--md"
            href={getUrlWithProtocol(agencyInfo.website)}
            target="_blank"
            rel="noreferrer"
            tabIndex={0}
          >
            {agencyInfo.website}
          </a>
        </span>
      )}
    </p>
  );
};

const AgencyDetails: React.FC<React.PropsWithChildren<IAgencyDetailsProps>> = ({
  agency,
  content,
  subHeaderComponent,
}: IAgencyDetailsProps) => {
  const {
    address,
    address2,
    phone,
    phone2,
    phone3,
    phone4,
    mobilePhone,
    mobilePhone2,
    email,
    email2,
    website,
    website2,
  } = agency;

  const primaryInfo = {
    agencyAddress: address,
    phone1: phone,
    phone2,
    mobilePhone,
    email,
    website,
  };
  const secondaryInfo = {
    agencyAddress: address2,
    phone1: phone3,
    phone2: phone4,
    mobilePhone: mobilePhone2,
    email: email2,
    website: website2,
  };

  return (
    <div className="c-agency-details">
      <div
        className="c-agency-details__primary-text"
        data-testid={dataTestIds.profile["MainPage.AgencyDetails.field"]}
      >
        {agency.description && <div>{agency.description}</div>}
        <h5 className="c-agency-details__agency-name">{agency.name}</h5>
        {subHeaderComponent}
      </div>
      {renderAgencyInfo(primaryInfo, content)}
      {agency.revealAddress2 && renderAgencyInfo(secondaryInfo, content)}
    </div>
  );
};

export default AgencyDetails;
